import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_CONFIG } from '../config';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, verifyOtp } = useAuth();
  const [formData, setFormData] = useState({
    username: searchParams.get('username') || '',
    password: '',
  });
  const [otpCode, setOtpCode] = useState('');
  const [otpSession, setOtpSession] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const setupMode = searchParams.get('setup') === '1';

  useEffect(() => {
    const username = searchParams.get('username');
    if (username) {
      setFormData((prev) => ({ ...prev, username }));
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const postLoginNavigate = () => {
    if (setupMode) {
      navigate('/profile/change-password?setup=1');
    } else {
      navigate('/dashboard');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const form = e.currentTarget;
    const credentials = {
      username: (form.username?.value ?? formData.username).trim(),
      password: (form.password?.value ?? formData.password).trim(),
    };
    setFormData(credentials);

    try {
      const result = await login(credentials);
      if (result.requiresOtp) {
        setOtpSession({
          otpSessionId: result.otpSessionId,
          maskedEmail: result.maskedEmail,
        });
        setOtpCode('');
        setError('');
      } else if (result.success) {
        postLoginNavigate();
      } else {
        setError(result.error || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (!otpSession?.otpSessionId) return;
    setError('');
    setLoading(true);

    try {
      const result = await verifyOtp({
        otpSessionId: otpSession.otpSessionId,
        otp: otpCode.trim(),
      });
      if (result.success) {
        postLoginNavigate();
      } else {
        setError(result.error || 'Invalid verification code');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setOtpSession(null);
    setOtpCode('');
    setError('');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)',
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 2,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <img
              src="/Etisalat_eand_Logo_EN_red.png"
              alt="Etisalat Logo"
              style={{ width: '100px', marginBottom: '16px' }}
            />
            <Typography variant="h4" component="h1" gutterBottom>
              {API_CONFIG.PORTAL_TITLE}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {API_CONFIG.IS_ADMIN_PORTAL
                ? 'IT Admin and Group Company Secretary access'
                : 'Company Secretary access'}
            </Typography>
          </Box>

          {setupMode && !otpSession && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Use the temporary password from your welcome email to sign in, then you will be asked to set a new password.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {otpSession ? (
            <form onSubmit={handleOtpSubmit}>
              <Alert severity="info" sx={{ mb: 2 }}>
                A verification code was sent to <strong>{otpSession.maskedEmail}</strong>.
                Enter the 6-digit code to continue.
              </Alert>
              <TextField
                fullWidth
                label="Verification code"
                name="otp"
                value={otpCode}
                onChange={(e) => {
                  setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                  setError('');
                }}
                margin="normal"
                required
                autoFocus
                disabled={loading}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                disabled={loading || otpCode.length < 6}
                sx={{ mt: 3, mb: 1 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Verify & Sign In'}
              </Button>
              <Button
                type="button"
                fullWidth
                variant="text"
                disabled={loading}
                onClick={handleBackToLogin}
              >
                Back to sign in
              </Button>
            </form>
          ) : (
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                margin="normal"
                required
                autoFocus
                disabled={loading}
              />
              <TextField
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                margin="normal"
                required
                disabled={loading}
              />
              <Box sx={{ textAlign: 'right', mt: 0.5 }}>
                <Link component={RouterLink} to="/forgot-password" variant="body2">
                  Forgot password?
                </Link>
              </Box>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                disabled={loading}
                sx={{ mt: 3, mb: 1 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>
            </form>
          )}

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="caption" color="textSecondary">
              {API_CONFIG.PORTAL_TITLE}
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
